/**
 * practice/types — the shared vocabulary of TEST MEETINGS (code: practice mode).
 *
 * A test meeting runs the REAL meeting screens — MeetingProgram, the weekly L10
 * sections, the quarterly room and everything they embed — against a meeting
 * that exists only in this browser tab. Reads of the company's data (rocks,
 * issues, the scorecard, the V/TO, the people chart, the saved agenda) go to
 * the server as usual; every WRITE the screens make is answered locally by the
 * practice engine and never leaves the tab. Nothing is saved.
 *
 * The three layers:
 *   · `practiceGuard.ts`  — axios interceptors on the app's clients. While a
 *     test meeting is open it routes each request to the engine: served
 *     locally, or passed through (reads), or REFUSED (a write the engine does
 *     not simulate, on a meeting-domain path).
 *   · `practiceEngine.ts` — the fake server: the practice state, the route
 *     table, persistence to sessionStorage (so a refresh keeps your place).
 *   · `routes/*.ts`        — one file per domain, each a `PracticeRoute[]`
 *     mirroring the backend route it stands in for, response shape and all.
 */
import type { L10Attendee, L10Entry, L10Meeting, IssueListItem } from '../eosL10Api';
import type { PeopleReview, RoomPlan } from '../eosQuarterlyApi';

/** Every practice meeting id starts with this — the server never mints one. */
export const PRACTICE_ID_PREFIX = 'practice-';

/** Ids the engine mints for things created during a test meeting. */
export const PRACTICE_ISSUE_PREFIX = 'practice-issue-';
export const PRACTICE_ENTRY_PREFIX = 'practice-entry-';
export const PRACTICE_COMMENT_PREFIX = 'practice-comment-';
export const PRACTICE_PLAN_PREFIX = 'practice-plan-';
/**
 * To-dos have INTEGER ids in the app, so practice to-dos take ids from this
 * floor upward — far above any real row, and positive, because some screens
 * treat a non-positive id as "no to-do".
 */
export const PRACTICE_TODO_ID_FLOOR = 900_000_000;

export type PracticeKind = 'weekly_l10' | 'quarterly' | 'annual';

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete' | 'head' | 'options';

/** One request, normalised by the guard before the engine sees it. */
export interface PracticeRequest {
  method: HttpMethod;
  /**
   * The full path, query stripped, always beginning `/api` —
   * e.g. `/api/eos-l10/meetings/practice-q-1234/start`. The client's baseURL
   * is already folded in, so a route never cares which axios instance sent it.
   */
  path: string;
  /** Query parameters as strings (axios `params` plus any `?query` on the url). */
  query: Record<string, string>;
  /** The JSON body, parsed. `undefined` when there is none. */
  body: any;
}

/** A locally-served response. `status` defaults to 200. */
export interface PracticeReply {
  status?: number;
  /** The response BODY — for this app, almost always `{ success, data }` / `{ success: false, error }`. */
  data: any;
}

/** Everything a test meeting is built from — assembled by the host before the engine starts. */
export interface PracticeSeed {
  kind: PracticeKind;
  /** The practice meeting's id, `practice-…` (see `practiceMeetingId`). */
  meetingId: string;
  teamId: string;
  teamName: string;
  /** The quarterly / annual session the room reads (null on a weekly L10). */
  sessionId: string | null;
  title: string;
  /** The Monday (YYYY-MM-DD) the weekly's week-keyed reads use; null for the quarterly. */
  weekKey: string | null;
  scheduledFor: string | null;
  /** The person running the test meeting (the signed-in user). */
  viewer: { user_id: number; display_name: string };
  /** The name directory — every person the test meeting may need to name (team, attendees, owners). */
  people: Array<{ user_id: number; display_name: string }>;
  /**
   * The attendee rows the meeting starts with — the session's attendees on a
   * quarterly (the real room seeds them the same way), empty on a weekly (the
   * real weekly only lists who has joined). The viewer joins on open either way.
   */
  attendeeIds: number[];
}

/** A to-do created during a test meeting — shaped like the todo rows the screens read. */
export interface PracticeTodo {
  id: number;
  title: string;
  description: string | null;
  assigned_to: number | null;
  assigned_to_name: string | null;
  due_date: string | null;
  priority: string | null;
  completed_at: string | null;
  /**
   * `eos_l10` = made in the meeting; `eos_issue` = made while solving an issue;
   * `manual` = left standalone when the issue it came from was DELETED (the
   * server re-homes a deleted issue's to-dos the same way).
   */
  source_type: 'eos_l10' | 'eos_issue' | 'manual';
  /** The practice meeting id, the issue id it was solved from, or null once standalone. */
  source_id: string | null;
  created_by: number | null;
  created_at: string;
}

export interface PracticeComment {
  id: string;
  content: string;
  created_at: string;
  author_name: string | null;
  user_id: number;
}

/** One line of the engine's own record: what the test meeting did instead of saving. */
export interface PracticeLogLine {
  at: string;
  method: HttpMethod;
  path: string;
  outcome: 'simulated' | 'refused';
}

/**
 * THE PRACTICE STATE — everything a test meeting has "changed", kept in the
 * tab. Serializable (it round-trips through sessionStorage), so no functions,
 * Maps or Dates in here.
 *
 * Real records are never copied wholesale: the state holds only the DELTA —
 * what was created, and what was changed on a real row — and the routes
 * overlay that delta onto the server's live read each time. So a real rock or
 * issue edited by someone else mid-test still shows their change.
 */
export interface PracticeState {
  version: 1;
  seed: PracticeSeed;
  /** The practice meeting row, in exactly the shape `eosL10Api` returns. */
  meeting: L10Meeting;
  /** RAW attendee rows (ratings unsealed). The meeting routes seal them on read. */
  attendees: L10Attendee[];
  entries: L10Entry[];
  issues: {
    /** Raised during the test meeting (ids `practice-issue-…`). */
    created: IssueListItem[];
    /** Changes to REAL issues, by id — merged over the live row on read. */
    patched: Record<string, Partial<IssueListItem>>;
    /** The viewer's vote on an issue as the test meeting left it (true = voted). */
    myVotes: Record<string, boolean>;
    /** Comments added during the test meeting, by issue id. */
    comments: Record<string, PracticeComment[]>;
    /** When this test closed its ballot. A test never closes, or reopens, the real one. */
    votingClosedAt?: string | null;
  };
  todos: {
    created: PracticeTodo[];
    /** REAL to-dos completed / reopened during the test meeting: id → completed_at (null = reopened). */
    completion: Record<string, string | null>;
  };
  rocks: {
    /** REAL rocks' status as the test meeting set it: id → status. */
    status: Record<string, string>;
  };
  /** The People grid as the test meeting last "saved" it (quarterly); null = untouched. */
  peopleReviews: PeopleReview[] | null;
  /** The next-quarter plan as the test meeting "committed" it (quarterly); null = none. */
  plan: RoomPlan | null;
  /** Monotonic counter behind every minted id. */
  seq: number;
  log: PracticeLogLine[];
}

/** What a route handler gets: the state, a way to change it, and the real server for reads. */
export interface PracticeContext {
  /** The current state. Read it freely; change it ONLY inside `update`. */
  readonly state: PracticeState;
  /** Mutate the state (in place, inside the callback), then persist + notify subscribers. */
  update(mutate: (draft: PracticeState) => void): void;
  /**
   * A REAL read from the server, bypassing the guard — `path` is a full
   * `/api/...` path. Resolves to the response BODY (the `{ success, data }`
   * envelope); rejects like axios on a non-2xx.
   */
  fetchReal<T = any>(path: string, query?: Record<string, unknown>): Promise<T>;
  /** Now, as an ISO string (injectable in tests). */
  now(): string;
  /** Mint a string id: `${prefix}${seq}` — unique within this test meeting. */
  mintId(prefix: string): string;
  /** Mint a practice to-do id (≥ PRACTICE_TODO_ID_FLOOR). */
  mintTodoId(): number;
  /** Display name for a user id from the seed's people (null if unknown). */
  nameOf(userId: number | null | undefined): string | null;
}

export type RouteParams = Record<string, string>;

/**
 * One simulated backend route. `pattern` is Express-style over the FULL path
 * (`/api/eos-l10/meetings/:id/start`).
 *
 *   · `serve`   — answer the request locally. Return `undefined` to decline
 *                 (e.g. a route that only simulates practice ids): a declined
 *                 GET goes to the server; a declined write falls to the guard's
 *                 refusal rule.
 *   · `overlay` — GET only: rewrite the SERVER's response body so it includes
 *                 what the test meeting changed. Runs after a declined/absent
 *                 `serve`. Must return the (possibly new) body.
 */
export interface PracticeRoute {
  method: HttpMethod;
  pattern: string;
  serve?: (ctx: PracticeContext, req: PracticeRequest, params: RouteParams) =>
    PracticeReply | undefined | Promise<PracticeReply | undefined>;
  overlay?: (ctx: PracticeContext, req: PracticeRequest, params: RouteParams, body: any) => any;
}

/** The `{ success: true, data }` envelope every route answers with. */
export const ok = (data: unknown, status = 200): PracticeReply => ({ status, data: { success: true, data } });

/** The `{ success: false, error }` envelope — shown verbatim by `extractApiError`. */
export const fail = (status: number, error: string): PracticeReply => ({ status, data: { success: false, error } });

/** True for an id the practice engine minted (a meeting, an issue, an entry…). */
export const isPracticeId = (id: unknown): boolean =>
  typeof id === 'string' && id.startsWith(PRACTICE_ID_PREFIX);

/** True for a to-do id the practice engine minted. */
export const isPracticeTodoId = (id: unknown): boolean =>
  Number(id) >= PRACTICE_TODO_ID_FLOOR;

/** The practice meeting id for a kind + target (a team id for the weekly, a session id otherwise). */
export function practiceMeetingId(kind: PracticeKind, targetId: string): string {
  const short = kind === 'weekly_l10' ? 'w' : kind === 'annual' ? 'a' : 'q';
  return `${PRACTICE_ID_PREFIX}${short}-${targetId}`;
}
